import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useElapsedTime } from '@/hooks/useElapsedTime';
import { format } from 'date-fns';

const REFRESH_INTERVAL_MS = 60_000;

interface WORow {
  id: string;
  wo_id: string;
  type: string;
  status: string;
  impact?: string;
  created_at: string;
  start_time?: string;
  failure_text?: string;
  problem_description?: string;
  mold?: { mold_id: string; name: string };
  assignee?: { name: string };
}

interface PMRow {
  mold_id: string;
  next_due_date: string;
  molds?: { mold_id: string; name: string };
}

interface KioskData {
  org_name: string;
  refreshed_at: string;
  line_down: WORow[];
  in_progress: WORow[];
  aging: WORow[];
  pm_due_week: PMRow[];
}

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return <span>{format(now, 'HH:mm:ss')}</span>;
}

function ElapsedBadge({ startTime }: { startTime?: string }) {
  const elapsed = useElapsedTime(startTime ?? null);
  if (!elapsed) return null;
  return <span className="font-mono text-sm opacity-70">{elapsed}</span>;
}

function WOCard({ wo, variant }: { wo: WORow; variant: 'red' | 'amber' | 'blue' }) {
  const bg = variant === 'red' ? 'bg-red-950 border-red-500' : variant === 'amber' ? 'bg-amber-950 border-amber-500' : 'bg-slate-800 border-slate-600';
  return (
    <div className={`rounded-lg border ${bg} px-4 py-3 flex items-center justify-between gap-4`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-bold text-white">{wo.mold?.mold_id ?? '—'}</span>
          <span className="text-slate-400 text-sm">{wo.wo_id}</span>
        </div>
        <p className="text-sm text-slate-300 truncate">
          {wo.failure_text || wo.problem_description || '—'}
        </p>
      </div>
      <div className="text-right shrink-0">
        {wo.assignee?.name && (
          <p className="text-xs text-slate-400 mb-0.5">{wo.assignee.name}</p>
        )}
        <ElapsedBadge startTime={wo.start_time} />
      </div>
    </div>
  );
}

export default function KioskDisplay() {
  const [searchParams] = useSearchParams();
  const orgSlug = searchParams.get('org') ?? '';
  const [data, setData] = useState<KioskData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const load = useCallback(async () => {
    if (!orgSlug) { setError('No org specified. Add ?org=<slug> to the URL.'); return; }
    try {
      const res = await fetch(`/api/kiosk?org=${encodeURIComponent(orgSlug)}`);
      if (!res.ok) { setError(`Error ${res.status}`); return; }
      const json = await res.json() as KioskData;
      setData(json);
      setLastRefresh(new Date());
      setError(null);
    } catch {
      setError('Network error — retrying…');
    }
  }, [orgSlug]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <p className="text-red-400 text-lg">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-slate-400 text-lg animate-pulse">Loading…</div>
      </div>
    );
  }

  const hasLineDown = data.line_down.length > 0;

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6 select-none">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 border-b border-slate-800 pb-4">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="MoldOp" className="h-8 opacity-80" />
          <span className="text-slate-400 text-sm font-medium">{data.org_name}</span>
        </div>
        <div className="text-right">
          <div className="text-2xl font-mono font-bold text-white"><Clock /></div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Updated {format(lastRefresh, 'HH:mm')}
          </div>
        </div>
      </div>

      {/* Line Down alert — full-width urgent banner */}
      {hasLineDown && (
        <div className="mb-6 rounded-xl border-2 border-red-500 bg-red-950/60 px-5 py-4">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-3 w-3 rounded-full bg-red-500 animate-pulse shrink-0" />
            <h2 className="text-lg font-bold text-red-400 uppercase tracking-wide">
              Line Down — {data.line_down.length} Active
            </h2>
          </div>
          <div className="space-y-2">
            {data.line_down.map(wo => <WOCard key={wo.id} wo={wo} variant="red" />)}
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* In Progress */}
        <div className="lg:col-span-2">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Work in Progress
            <span className="ml-2 text-white">{data.in_progress.length}</span>
          </h2>
          {data.in_progress.length === 0 ? (
            <p className="text-slate-600 text-sm">No work orders in progress</p>
          ) : (
            <div className="space-y-2">
              {data.in_progress.map(wo => <WOCard key={wo.id} wo={wo} variant="blue" />)}
            </div>
          )}

          {/* Aging */}
          {data.aging.length > 0 && (
            <div className="mt-6">
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
                Needs Attention
                <span className="ml-2 text-amber-400">{data.aging.length}</span>
              </h2>
              <div className="space-y-2">
                {data.aging.map(wo => <WOCard key={wo.id} wo={wo} variant="amber" />)}
              </div>
            </div>
          )}
        </div>

        {/* PM Due This Week */}
        <div>
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
            PM Due This Week
            <span className="ml-2 text-white">{data.pm_due_week.length}</span>
          </h2>
          {data.pm_due_week.length === 0 ? (
            <p className="text-slate-600 text-sm">No PMs due this week</p>
          ) : (
            <div className="space-y-2">
              {data.pm_due_week.map(pm => (
                <div key={pm.mold_id} className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3">
                  <div className="font-semibold text-white">
                    {pm.molds?.mold_id ?? '—'}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    Due {new Date(pm.next_due_date).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
